// Tools that create elements: shapes, connectors, text, sticky notes, cards, frames and stickers.
import type {
  CardElement, FrameElement, LineElement, NoteElement, SepiaElement, ShapeElement, StickerElement, TextElement
} from "../../../shared/model";
import type { Editor, PointerInfo, Tool, ToolId } from "../editor";
import { boxOf, newId, newSeed, routeConnector } from "../elements";
import { type Vec, boxFromPoints, center, dist, rotate } from "../geometry";
import { measureText } from "../measure";
import { contrastInk, FONTS } from "../style";

/** the most a tap in keyboard mode zooms in to make the letters readable */
const MAX_READ_ZOOM = 3;

export function createTextAt(ed: Editor, p: Vec): void {
  const st = ed.style;
  const el: TextElement = {
    id: newId(), type: "text", x: p.x, y: p.y - st.fontSize * 0.7, w: 40, h: st.fontSize * FONTS[st.font].lineHeight * FONTS[st.font].scale,
    rot: 0, z: "", t: Date.now(), text: "", font: st.font, fontSize: st.fontSize, color: st.textColor, align: st.align, autoW: true
  };
  ed.insert([el]);
  ed.startEditing(el.id);
}

function finishCreate(ed: Editor, id: string, edit: boolean): void {
  if (edit) ed.startEditing(id);
  if (!ed.settings.toolLock && ed.ui.tool !== "sticker") ed.setUi({ tool: "select" });
}

/** Shared drag-to-size behaviour. */
abstract class DragCreateTool implements Tool {
  abstract readonly id: ToolId;
  protected start: Vec | null = null;
  protected current: SepiaElement | null = null;

  constructor(protected editor: Editor) {}

  cursor(): string {
    return "crosshair";
  }

  protected abstract make(box: { x: number; y: number; w: number; h: number }, tap: boolean, p: PointerInfo): SepiaElement;
  protected editAfter = false;

  onDown(p: PointerInfo): void {
    this.editor.stopEditing();
    this.editor.clearSelection();
    this.start = null;
    this.current = null;
    if (!this.editor.canDrawOnActiveLayer()) return;
    if (!this.editor.isOnPaper(p.world)) return this.editor.deskHint();
    this.start = p.world;
  }

  onMove(p: PointerInfo): void {
    if (!this.start) return;
    if (!this.current && dist(this.start, p.world) * this.editor.camera.z < 6) return;
    let end = p.world;
    if (p.shift) {
      const d = Math.max(Math.abs(end.x - this.start.x), Math.abs(end.y - this.start.y));
      end = { x: this.start.x + Math.sign(end.x - this.start.x || 1) * d, y: this.start.y + Math.sign(end.y - this.start.y || 1) * d };
    }
    const box = boxFromPoints([this.start, end]);
    const base = this.current ? { ...this.current, ...box } : this.make(box, false, p);
    this.current = { ...base, x: box.x, y: box.y, w: Math.max(4, box.w), h: Math.max(4, box.h), z: "~draft" } as SepiaElement;
    this.editor.setTransient([this.current]);
  }

  onUp(p: PointerInfo): void {
    const ed = this.editor;
    if (!this.start) return;
    let el: SepiaElement;
    if (this.current) el = { ...this.current };
    else el = this.make({ x: p.world.x, y: p.world.y, w: 0, h: 0 }, true, p);
    ed.setTransient(null);
    this.start = null;
    this.current = null;
    ed.insert([el]);
    finishCreate(ed, el.id, this.editAfter);
  }

  onCancel(): void {
    this.start = null;
    this.current = null;
    this.editor.setTransient(null);
  }

  deactivate(): void {
    this.onCancel();
  }
}

export class ShapeTool extends DragCreateTool {
  readonly id: ToolId = "shape";

  protected make(box: { x: number; y: number; w: number; h: number }, tap: boolean): ShapeElement {
    const st = this.editor.style;
    const w = tap ? (st.shapeKind === "ellipse" || st.shapeKind === "diamond" || st.shapeKind === "star" ? 140 : 180) : box.w;
    const h = tap ? (st.shapeKind === "rect" || st.shapeKind === "roundrect" || st.shapeKind === "parallelogram" ? 110 : 140) : box.h;
    const x = tap ? box.x - w / 2 : box.x;
    const y = tap ? box.y - h / 2 : box.y;
    const filled = st.fillStyle !== "none";
    return {
      id: newId(), type: "shape", kind: st.shapeKind, x, y, w, h, rot: 0, z: "", t: Date.now(), stroke: st.stroke,
      fill: st.fill, fillStyle: st.fillStyle, strokeWidth: st.strokeWidth, dash: st.dash, rough: st.rough, seed: newSeed(),
      text: "", font: st.font, fontSize: Math.min(st.fontSize, 28), align: "center",
      textColor: filled && st.fillStyle === "solid" ? contrastInk(st.fill) : st.textColor, shadow: false
    };
  }
}

export class FrameTool extends DragCreateTool {
  readonly id: ToolId = "frame";

  protected make(box: { x: number; y: number; w: number; h: number }, tap: boolean): FrameElement {
    const n = this.editor.doc.all().filter((e) => e.type === "frame").length + 1;
    const w = tap ? 720 : box.w;
    const h = tap ? 480 : box.h;
    return {
      id: newId(), type: "frame", x: tap ? box.x - w / 2 : box.x, y: tap ? box.y - h / 2 : box.y, w, h, rot: 0,
      z: this.editor.zBottom(1)[0], t: Date.now(), title: `Page ${n}`, color: "#d9b25a"
    };
  }

  onUp(p: PointerInfo): void {
    // frames sit underneath everything
    const ed = this.editor;
    if (!this.start) return;
    const el = this.current ? { ...this.current } : this.make({ x: p.world.x, y: p.world.y, w: 0, h: 0 }, true);
    ed.setTransient(null);
    this.start = null;
    this.current = null;
    const z = ed.zBottom(1)[0];
    ed.commit({ puts: [{ ...el, z, layer: ed.activeLayerId() } as SepiaElement] });
    ed.setUi({ selection: [el.id] });
    finishCreate(ed, el.id, false);
  }
}

export class NoteTool extends DragCreateTool {
  readonly id: ToolId = "note";
  protected editAfter = true;

  protected make(box: { x: number; y: number; w: number; h: number }, tap: boolean): NoteElement {
    const st = this.editor.style;
    const s = 200;
    return {
      id: newId(), type: "note", x: tap ? box.x - s / 2 : box.x, y: tap ? box.y - s / 2 : box.y, w: tap ? s : box.w, h: tap ? s : box.h,
      rot: ((Math.random() - 0.5) * 3 * Math.PI) / 180, z: "", t: Date.now(), text: "", paper: st.notePaper,
      font: st.font === "hand" || st.font === "print" || st.font === "marker" ? st.font : "hand", fontSize: 30, textColor: "#2b211a",
      align: "left", tape: st.tape, shadow: st.shadow
    };
  }
}

export class CardTool extends DragCreateTool {
  readonly id: ToolId = "card";
  protected editAfter = true;

  protected make(box: { x: number; y: number; w: number; h: number }, tap: boolean): CardElement {
    const st = this.editor.style;
    const w = tap ? 360 : Math.max(160, box.w);
    const h = tap ? 260 : Math.max(100, box.h);
    return {
      id: newId(), type: "card", x: tap ? box.x - w / 2 : box.x, y: tap ? box.y - h / 2 : box.y, w, h, rot: 0, z: "", t: Date.now(),
      md: "", paper: st.cardPaper, accent: "#c2583a", fontSize: 17, shadow: true
    };
  }
}

export class TextTool implements Tool {
  readonly id: ToolId = "text";
  constructor(private editor: Editor) {}
  cursor(): string {
    return "text";
  }
  private down: PointerInfo | null = null;
  onDown(p: PointerInfo): void {
    this.down = p;
  }
  onMove(): void {}
  onUp(): void {
    // on pointer-up, so iPadOS will show the keyboard
    const p = this.down;
    this.down = null;
    if (p) this.type(p);
  }
  /** a finger tap while fingers move the page (keyboard mode, or pencil-only) */
  onTap(p: PointerInfo): void {
    this.type(p);
  }
  /** Edit the text under `p`, or start a new line of text there. */
  private type(p: PointerInfo): void {
    const ed = this.editor;
    const hit = ed.hitAt(p.world, 6, { filter: (e) => e.type === "text" || e.type === "note" || e.type === "card" || e.type === "shape" });
    if (hit) {
      ed.startEditing(hit.id);
      if (ed.keyboardMode) this.readable(p.screen, p.world, "fontSize" in hit && typeof hit.fontSize === "number" ? hit.fontSize : ed.style.fontSize);
      return;
    }
    ed.stopEditing();
    ed.clearSelection();
    if (!ed.isOnPaper(p.world)) return ed.deskHint();
    createTextAt(ed, p.world);
    if (ed.keyboardMode) this.readable(p.screen, p.world, ed.style.fontSize);
    else if (!ed.settings.toolLock) ed.setUi({ tool: "select" });
  }
  /** A key typed in keyboard mode with no text box open: start a new line under the last one (or on screen). */
  typeKey(key: string): void {
    const ed = this.editor;
    const cam = ed.camera;
    const view = cam.view(-24);
    const last = ed.lastTextId ? ed.doc.get(ed.lastTextId) : undefined;
    let at: Vec | null = null;
    if (last?.type === "text") {
      const below = { x: last.x, y: last.y + last.h + last.fontSize * 0.95 };
      if (below.x >= view.x && below.x <= view.x + view.w && below.y >= view.y && below.y <= view.y + view.h && ed.isOnPaper(below)) at = below;
    }
    at ??= cam.toWorld({ x: cam.width * 0.3, y: cam.height * 0.35 });
    if (!ed.isOnPaper(at)) return ed.deskHint();
    createTextAt(ed, at);
    const id = ed.ui.editingId;
    const el = id ? ed.doc.get(id) : undefined;
    if (!id || el?.type !== "text") return;
    const m = measureText(key, el.font, el.fontSize, { bold: el.bold, italic: el.italic });
    ed.update(id, (cur) => ({ ...(cur as TextElement), text: key, w: Math.max(20, m.w), h: Math.max(m.h, el.fontSize) }), "type-" + id);
    this.readable(cam.toScreen(at), at, el.fontSize);
  }
  /** Typing on a phone at 20% zoom would make letters a few pixels tall: zoom in around the tap until they read. */
  private readable(screen: Vec, world: Vec, fontSize: number): void {
    const cam = this.editor.camera;
    if (fontSize * cam.z >= 15) return;
    const z = Math.min(MAX_READ_ZOOM, 20 / fontSize);
    cam.animateTo({ x: screen.x - world.x * z, y: screen.y - world.y * z, z }, 200);
  }
  onCancel(): void {
    this.down = null;
  }
}

export class StickerTool implements Tool {
  readonly id: ToolId = "sticker";
  constructor(private editor: Editor, private sizeOf: (key: string) => { w: number; h: number } | null) {}
  cursor(): string {
    return "copy";
  }
  onDown(p: PointerInfo): void {
    const ed = this.editor;
    const key = ed.style.stickerKey;
    if (!key) return;
    if (!ed.isOnPaper(p.world)) return ed.deskHint();
    const nat = this.sizeOf(key) ?? { w: 1, h: 1 };
    const side = 150;
    const k = side / Math.max(nat.w, nat.h);
    const w = nat.w * k;
    const h = nat.h * k;
    const el: StickerElement = {
      id: newId(), type: "sticker", key, x: p.world.x - w / 2, y: p.world.y - h / 2, w, h,
      rot: ((Math.random() - 0.5) * 8 * Math.PI) / 180, z: "", t: Date.now()
    };
    // the sticker tool stays armed: tap again to place another; Esc or another tool ends it
    ed.insert([el]);
  }
  onMove(): void {}
  onUp(): void {}
  onCancel(): void {}
}

/** Connector: drag from one thing to another; ends attach to whatever they land on. */
export class LineTool implements Tool {
  readonly id: ToolId = "line";
  private start: Vec | null = null;
  private startBind: { id: string; ax: number; ay: number } | null = null;
  private draft: LineElement | null = null;

  constructor(private editor: Editor) {}

  cursor(): string {
    return "crosshair";
  }

  private attach(p: Vec, exclude?: string) {
    const ed = this.editor;
    const target = ed.hitAt(p, 8, { filter: (e) => e.type !== "line" && e.type !== "ink" && e.id !== exclude });
    if (!target) return null;
    const tb = boxOf(target);
    const local = rotate(p, -target.rot, center(tb));
    const ax = Math.min(1, Math.max(0, (local.x - tb.x) / tb.w));
    const ay = Math.min(1, Math.max(0, (local.y - tb.y) / tb.h));
    const inner = ax > 0.2 && ax < 0.8 && ay > 0.2 && ay < 0.8;
    return { id: target.id, ax: inner ? 0.5 : ax, ay: inner ? 0.5 : ay };
  }

  onDown(p: PointerInfo): void {
    this.editor.stopEditing();
    this.editor.clearSelection();
    this.start = null;
    this.draft = null;
    if (!this.editor.canDrawOnActiveLayer()) return;
    if (!this.editor.isOnPaper(p.world)) return this.editor.deskHint();
    this.start = p.world;
    this.startBind = this.attach(p.world);
  }

  onMove(p: PointerInfo): void {
    const ed = this.editor;
    if (!this.start) {
      const t = this.attach(p.world);
      ed.setOverlay({ anchorHint: t ? { id: t.id, p: p.world } : null });
      return;
    }
    if (!this.draft && dist(this.start, p.world) * ed.camera.z < 8) return;
    let end = p.world;
    if (p.shift) {
      const ang = Math.round(Math.atan2(end.y - this.start.y, end.x - this.start.x) / (Math.PI / 4)) * (Math.PI / 4);
      const l = dist(this.start, end);
      end = { x: this.start.x + Math.cos(ang) * l, y: this.start.y + Math.sin(ang) * l };
    }
    const endBind = this.attach(end, this.startBind?.id);
    this.draft = this.build(end, endBind);
    ed.setTransient([{ ...this.draft, z: "~draft" }]);
    ed.setOverlay({ anchorHint: endBind ? { id: endBind.id, p: end } : null });
  }

  private build(end: Vec, endBind: { id: string; ax: number; ay: number } | null): LineElement {
    const st = this.editor.style;
    const a = this.start!;
    const b = boxFromPoints([a, end]);
    let line: LineElement = {
      id: this.draft?.id ?? newId(), type: "line", x: b.x, y: b.y, w: Math.max(1, b.w), h: Math.max(1, b.h), rot: 0, z: "", t: Date.now(),
      pts: [[a.x - b.x, a.y - b.y], [end.x - b.x, end.y - b.y]], route: st.route,
      start: { bind: this.startBind?.id ?? null, ax: this.startBind?.ax, ay: this.startBind?.ay, head: st.startHead },
      end: { bind: endBind?.id ?? null, ax: endBind?.ax, ay: endBind?.ay, head: st.endHead },
      stroke: st.stroke, strokeWidth: st.strokeWidth, dash: st.dash, rough: st.rough, seed: this.draft?.seed ?? newSeed(),
      label: "", font: st.font, fontSize: 20
    };
    if (st.route === "curve") {
      const mid = { x: (a.x + end.x) / 2, y: (a.y + end.y) / 2 };
      const n = { x: -(end.y - a.y) * 0.18, y: (end.x - a.x) * 0.18 };
      const c = { x: mid.x + n.x, y: mid.y + n.y };
      const bb = boxFromPoints([a, c, end]);
      line = { ...line, x: bb.x, y: bb.y, w: Math.max(1, bb.w), h: Math.max(1, bb.h), pts: [[a.x - bb.x, a.y - bb.y], [c.x - bb.x, c.y - bb.y], [end.x - bb.x, end.y - bb.y]] };
    }
    return routeConnector(line, (id) => this.editor.doc.get(id));
  }

  onUp(): void {
    const ed = this.editor;
    ed.setTransient(null);
    ed.setOverlay({ anchorHint: null });
    if (this.draft) {
      const el = this.draft;
      ed.insert([el]);
      finishCreate(ed, el.id, false);
    }
    this.start = null;
    this.draft = null;
    this.startBind = null;
  }

  onCancel(): void {
    this.editor.setTransient(null);
    this.editor.setOverlay({ anchorHint: null });
    this.start = null;
    this.draft = null;
  }

  onHover(p: PointerInfo | null): void {
    if (!p) this.editor.setOverlay({ anchorHint: null });
    else if (!this.start) this.onMove(p);
  }

  deactivate(): void {
    this.onCancel();
  }
}
